import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { some } from "lodash";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import EmptyState from "./EmptyState";
import {
  DimensionsChart,
  DimensionsChartProps,
} from "@/shared/charts/DimensionsChart";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import { ToggleableSection } from "@/shared/ui/ToggleableSection";
import { DimensionContext } from "@/types/data";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";

interface CompetencyMapSectionProps
  extends Omit<DimensionsChartProps, "onClickSector" | "competencies"> {
  dimensions: any[];
  defaultPersonId?: number;
  title?: string;
  description?: string;
  positionCompetencies?: any[];
  context: DimensionContext;
}

const getScoring = (dimension: DimensionWithScores) => {
  if (!dimension?.score?.value) return null;
  if (dimension?.score?.value && dimension?.score?.value > 70) return "high";
  if (dimension?.score?.value && dimension?.score?.value > 30) return "medium";
  return "low";
};

function CompetencyMapSection(props: CompetencyMapSectionProps) {
  const {
    dimensions,
    defaultPersonId,
    positionCompetencies,
    description,
    title,
    context,
  } = props;
  const { t } = useTranslation();

  const [openCompetencyModal] = useModal(ModalFileName.DIMENSION_DETAILS);

  const handleSectorClick = (sector: any, allSectors: any[]) => {
    openCompetencyModal({
      dimension: sector,
      allDimensions: dimensions?.filter((d) => d.parent !== null), // do not show group dimensions
      context,
    });
  };

  const positionCompetenciesWithScoring = useMemo(
    () =>
      positionCompetencies?.map((competency) => {
        const dimension = dimensions?.find(
          (d) => d.id === competency.dimension_id
        );
        return {
          ...competency,
          scoring: getScoring(dimension),
        };
      }),
    [positionCompetencies, dimensions]
  );

  if (some(dimensions, (d) => !d.score)) return null;

  return (
    <section className="w-full">
      <SectionHeader
        title={t("profile.competency_map")}
        subtitle={t("profile.competency_map_description")}
      />
      <EmptyState
        variant="default"
        text="Please complete the competency survey."
        posNotConfed={false}
        visible={dimensions?.length === 0}
      />
      <ToggleableSection
        keepOpen={!positionCompetencies}
        defaultOpen={!positionCompetencies}
        triggerLabel="Show competency map"
      >
        <div className="h-[755px] w-full">
          <DimensionsChart
            dimensions={dimensions}
            defaultPersonId={defaultPersonId}
            onClickSector={handleSectorClick}
          />
        </div>
      </ToggleableSection>
    </section>
  );
}

export { CompetencyMapSection };
